/**
 * @fileoverview Harita Tabanli Arama Hook'u
 *
 * Bu modul, harita sinirlari (bounding box) icinde ilan aramak icin
 * kullanilan custom hook'u icerir. `emlakjet.map_search` MCP aracini
 * kullanarak belirli bir cografi alanda ilan arar.
 *
 * **Onemli:** Bu arac widget-only'dir - ChatGPT bu araci gormez ve
 * yanitlamaz. Sadece harita etkilesimi icin kullanilir.
 *
 * @module useMapSearch
 * @see {@link https://www.emlakjet.com} Emlakjet
 */

import { useState, useCallback } from "react";
import {
  extractStructuredContent,
  isCallToolAvailable,
  ERROR_MESSAGES,
  getBasePathWithoutLocation,
  notifyMapSearchResults,
} from "../utils";

/**
 * @typedef {Object} MapBounds
 * @property {string} bottom_left - Guneybati koordinati (lat,lng formatinda)
 * @property {string} top_right - Kuzeydogu koordinati (lat,lng formatinda)
 */

/**
 * @typedef {Object} UseMapSearchOptions
 * @property {Function} onSearchComplete - Arama tamamlandiginda callback
 * @property {Function} [onEmptyResults] - Sonuc yoksa callback
 * @property {Function} [onError] - Hata durumunda callback
 */

/**
 * @typedef {Object} UseMapSearchReturn
 * @property {boolean} isSearching - Arama devam ediyor mu
 * @property {Function} searchInBounds - Sinirlar icinde arama fonksiyonu
 */

/**
 * Harita sinirlari icinde ilan aramak icin custom hook.
 *
 * Bu hook, `emlakjet.map_search` MCP aracini kullanarak belirli bir
 * cografi bolge (bounding box) icinde ilan arar. Bu arac widget-only'dir,
 * yani ChatGPT bu araci gormez ve yanitlamaz.
 *
 * **Kullanim Senaryosu:**
 * Kullanici haritada surukleme/zoom yaptiginda veya "bu alanda ara"
 * butonuna tikladiginda harita sinirlari icinde arama yapilir.
 *
 * **Arama Mekanigi:**
 * 1. Path'ten konum bilgisi cikarilir (bbox bunu yapacak)
 * 2. `emlakjet.map_search` araci cagrilir
 * 3. Sonuclar callback ile bildirilir
 * 4. ChatGPT'ye sonuc ozeti gonderilir
 *
 * **Hata Yonetimi:**
 * - callTool API yoksa hata callback'i cagrilir
 * - Path veya bounds eksikse hata doner
 * - API hatalari yaklanir ve callback ile bildirilir
 *
 * @function useMapSearch
 * @param {UseMapSearchOptions} options - Hook opsiyonlari
 * @returns {UseMapSearchReturn} Arama durumu ve fonksiyonu
 *
 * @example
 * const { isSearching, searchInBounds } = useMapSearch({
 *   onSearchComplete: (results) => setOutput(results),
 *   onEmptyResults: () => showEmptyState(),
 *   onError: (msg) => setError(msg),
 * });
 *
 * // Harita hareket ettiginde
 * const handleBoundsChange = (bounds) => {
 *   searchInBounds({
 *     path: currentPath,
 *     filters: currentFilters,
 *     bounds,
 *     limit: 30,
 *   });
 * };
 */
export function useMapSearch({ onSearchComplete, onEmptyResults, onError }) {
  const [isSearching, setIsSearching] = useState(false);

  /**
   * Harita sinirlari icinde ilan arar.
   *
   * @async
   * @function searchInBounds
   * @param {Object} params - Arama parametreleri
   * @param {string} params.path - Mevcut arama yolu (ornegin: "satilik-konut/istanbul")
   * @param {string} [params.filters] - Filtre string'i
   * @param {MapBounds} params.bounds - Harita sinirlari
   * @param {number} [params.limit=30] - Maksimum sonuc sayisi
   * @returns {Promise<void>}
   */
  const searchInBounds = useCallback(
    async ({ path, filters, bounds, limit = 30 }) => {
      if (!isCallToolAvailable()) {
        onError?.(ERROR_MESSAGES.CALL_TOOL_UNAVAILABLE);
        return;
      }

      if (!path) {
        onError?.("Arama yolu (path) bulunamadı.");
        return;
      }

      if (!bounds?.bottom_left || !bounds?.top_right) {
        onError?.("Harita sınırları bulunamadı.");
        return;
      }

      setIsSearching(true);

      try {
        // Use base path without location - bbox will handle geographic filtering
        const basePath = getBasePathWithoutLocation(path);
        if (!basePath) {
          onError?.("Geçersiz arama yolu.");
          setIsSearching(false);
          return;
        }

        const resp = await window.openai.callTool("emlakjet.map_search", {
          path: basePath,
          filters: filters || undefined,
          bottom_left: bounds.bottom_left,
          top_right: bounds.top_right,
          limit,
        });

        const structured = extractStructuredContent(resp);
        if (structured?.kind === "listings") {
          // Notify ChatGPT about map search results
          notifyMapSearchResults(structured, bounds);

          const hasListings =
            Array.isArray(structured.listings) && structured.listings.length > 0;
          if (hasListings) {
            onSearchComplete?.(structured);
          } else {
            // 0 results - call empty results handler
            onEmptyResults?.();
          }
        }
      } catch (e) {
        onError?.(e instanceof Error ? e.message : "Harita araması başarısız");
      } finally {
        setIsSearching(false);
      }
    },
    [onSearchComplete, onEmptyResults, onError]
  );

  return {
    isSearching,
    searchInBounds,
  };
}

export default useMapSearch;
